import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  apiErrorStatus,
  inspectionModificationService,
  MODIFICATION_TEXT_MAX,
  type ModificationEntry,
  type ModificationRound,
  type OpsFeasibilityVerdict,
  type OpsScopeImpact,
  type RecordOpsVerdictPayload,
} from '@/services/inspection-modification.service';
import {
  CLASSIFICATION_LABELS,
  COMMERCIAL_OUTCOME_LABELS,
  currentRoundOf,
  describeWhereRoundIs,
  ENTRY_TYPE_LABELS,
  operationsViewOf,
  OPS_VERDICT_HINTS,
  OPS_VERDICT_LABELS,
  OPS_VERDICTS,
  opsVerdictRequires,
  ROUND_STATE_BADGE,
  ROUND_STATE_LABELS,
  SCOPE_IMPACT_LABELS,
  SCOPE_IMPACTS,
  WAITING_FOR_LABELS,
} from '@/features/inspection/modification-state-rules';
import { SkeletonCard } from '@/components/skeletons';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { FormField } from '@/components/shared/FormField';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useHasPermission } from '@/hooks/use-permissions';
import { useAuthStore } from '@/stores/auth.store';
import { toast, swalConfirm } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';

/**
 * D-131 M5 — the Operations "Feasibility" panel (design §7, §21.5; readiness §14.3).
 *
 * ── WHAT THIS PANEL IS ──────────────────────────────────────────────────────
 * Commercial owns the customer conversation. Operations is asked exactly one
 * question about one round — "is this technically possible, and what does it
 * change?" — and this panel exists to answer it quickly and hand the round
 * straight back. It shows the customer's words, the classification, where the
 * round is, and (only while the round is on Operations' desk) the verdict form.
 *
 * ── WHAT IT DELIBERATELY DOES NOT HAVE ──────────────────────────────────────
 * No price, discount or amount field (none exists in the domain). No send, no
 * close, no message-the-customer control, no classification editor, no
 * quotation upload. Operations' note is INTERNAL until Commercial publishes it,
 * and the form says so. The backend enforces all of this; the panel only keeps
 * the screen honest about it.
 *
 * ── AUTHORIZATION ───────────────────────────────────────────────────────────
 * Hiding the form when the user lacks `inspection:modification:review` is a
 * UX courtesy. The server's PermissionGuard (403) and the state machine (409)
 * are the boundary, and both responses are handled explicitly below.
 */

const SELECT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

export const MODIFICATION_ROUNDS_KEY = 'modification-rounds';
export const MODIFICATION_ENTRIES_KEY = 'modification-round-entries';
export const QUOTATION_REVISIONS_KEY = 'quotation-revisions';

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

function StateBadge({ round }: { round: ModificationRound }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ROUND_STATE_BADGE[round.state]}`}
    >
      {ROUND_STATE_LABELS[round.state]}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

interface VerdictFormState {
  verdict: OpsFeasibilityVerdict | null;
  technicalNote: string;
  alternativeDescription: string;
  clarificationQuestion: string;
  scopeImpact: OpsScopeImpact | '';
  affectsParts: boolean;
  affectsLabour: boolean;
}

const EMPTY_FORM: VerdictFormState = {
  verdict: null,
  technicalNote: '',
  alternativeDescription: '',
  clarificationQuestion: '',
  scopeImpact: '',
  affectsParts: false,
  affectsLabour: false,
};

type FormErrors = Partial<Record<keyof VerdictFormState, string>>;

function validate(form: VerdictFormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.verdict) errors.verdict = 'Choose a feasibility response.';
  if (!form.technicalNote.trim()) errors.technicalNote = 'A technical explanation is required.';
  else if (form.technicalNote.length > MODIFICATION_TEXT_MAX)
    errors.technicalNote = `Keep the explanation under ${MODIFICATION_TEXT_MAX} characters.`;
  if (form.verdict) {
    const needs = opsVerdictRequires(form.verdict);
    if (needs.alternativeDescription && !form.alternativeDescription.trim())
      errors.alternativeDescription = 'Describe the alternative that would work.';
    if (needs.clarificationQuestion && !form.clarificationQuestion.trim())
      errors.clarificationQuestion = 'Write the question the customer needs to answer.';
  }
  return errors;
}

function toPayload(
  form: VerdictFormState,
  verdict: OpsFeasibilityVerdict,
): RecordOpsVerdictPayload {
  const needs = opsVerdictRequires(verdict);
  const payload: RecordOpsVerdictPayload = { verdict, technicalNote: form.technicalNote.trim() };
  if (needs.alternativeDescription)
    payload.alternativeDescription = form.alternativeDescription.trim();
  if (needs.clarificationQuestion)
    payload.clarificationQuestion = form.clarificationQuestion.trim();
  if (!needs.clarificationQuestion) {
    if (form.scopeImpact) payload.scopeImpact = form.scopeImpact;
    payload.affectsParts = form.affectsParts;
    payload.affectsLabour = form.affectsLabour;
  }
  return payload;
}

/** One professional sentence per failure the workflow can honestly produce. */
function describeWriteFailure(error: unknown, fallback: string): string {
  switch (apiErrorStatus(error)) {
    case 403:
      return 'You do not have permission to respond to modification requests on this job.';
    case 404:
      return 'This modification request is no longer available on this job.';
    case 409:
      return 'This request has moved on since you opened it. The latest status is shown now.';
    default:
      return getApiErrorMessage(error, fallback);
  }
}

interface FeasibilityReviewPanelProps {
  inspectionId: string;
}

export function FeasibilityReviewPanel({ inspectionId }: FeasibilityReviewPanelProps) {
  const qc = useQueryClient();
  const can = useHasPermission();
  const myUserId = useAuthStore((s) => s.user?.userId ?? null);
  const canReview = can('inspection:modification:review');

  const roundsQuery = useQuery({
    queryKey: [MODIFICATION_ROUNDS_KEY, inspectionId],
    queryFn: () => inspectionModificationService.listRounds(inspectionId),
  });
  const revisionsQuery = useQuery({
    queryKey: [QUOTATION_REVISIONS_KEY, inspectionId],
    queryFn: () => inspectionModificationService.listRevisions(inspectionId),
  });

  const rounds = roundsQuery.data ?? [];
  const round = currentRoundOf(rounds);

  const entriesQuery = useQuery({
    queryKey: [MODIFICATION_ENTRIES_KEY, inspectionId, round?.id],
    queryFn: () => inspectionModificationService.listEntries(inspectionId, round!.id),
    enabled: !!round,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: [MODIFICATION_ROUNDS_KEY, inspectionId] });
    void qc.invalidateQueries({ queryKey: [MODIFICATION_ENTRIES_KEY, inspectionId] });
    void qc.invalidateQueries({ queryKey: ['ops-work-queue'] });
    void qc.invalidateQueries({ queryKey: ['inspection-timeline', inspectionId] });
  };

  if (roundsQuery.isLoading) {
    return (
      <div data-testid="feasibility-loading">
        <SkeletonCard count={1} minHeight={160} />
      </div>
    );
  }

  if (roundsQuery.isError) {
    if (apiErrorStatus(roundsQuery.error) === 403) {
      return (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3"
        >
          <FontAwesomeIcon icon={faLock} className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium text-foreground">
              Modification requests are restricted
            </p>
            <p className="text-xs text-muted-foreground">
              Your role does not include access to this job&rsquo;s modification requests.
            </p>
          </div>
        </div>
      );
    }
    return (
      <QueryErrorState
        subject="the modification request"
        onRetry={() => void roundsQuery.refetch()}
      />
    );
  }

  if (!round) {
    return (
      <p className="text-sm text-muted-foreground">
        No modification request has been raised on this job.
      </p>
    );
  }

  const view = operationsViewOf(round);
  const revisions = revisionsQuery.data ?? [];
  const againstRevision = revisions.find((r) => r.id === round.againstRevisionId) ?? null;
  const earlierRounds = rounds
    .filter((r) => r.id !== round.id)
    .sort((a, b) => b.roundNumber - a.roundNumber);
  const isMine = !!myUserId && round.assignedUserId === myUserId;

  return (
    <div className="space-y-5" data-testid="feasibility-panel" data-view={view}>
      {/* ── Status strip ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          Modification Request #{round.roundNumber}
        </span>
        <StateBadge round={round} />
        {round.waitingFor && view !== 'CLOSED' && (
          <span className="text-xs text-muted-foreground">
            Waiting on {WAITING_FOR_LABELS[round.waitingFor]}
          </span>
        )}
        {isMine && view === 'AWAITING_OPERATIONS' && (
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
            You are handling this request
          </span>
        )}
      </div>

      <p className="text-sm text-muted-foreground">{describeWhereRoundIs(round)}</p>

      {/* ── The request itself ────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What the customer asked for
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{round.requestText}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Requested {fmtDateTime(round.createdAt)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Classification">
          {round.classification
            ? CLASSIFICATION_LABELS[round.classification]
            : 'Not yet classified'}
        </Field>
        <Field label="Against quotation">
          {againstRevision ? (
            <>
              {againstRevision.quotationReference ?? 'No reference'}{' '}
              <span className="text-xs text-muted-foreground">
                (revision {againstRevision.revisionNumber}, sent{' '}
                {fmtDateTime(againstRevision.sentAt)})
              </span>
            </>
          ) : (
            '—'
          )}
        </Field>
        {round.classifiedAt && <Field label="Classified">{fmtDateTime(round.classifiedAt)}</Field>}
      </div>

      {/* ── What Operations is asked / has answered ───────────────────────── */}
      {view === 'AWAITING_OPERATIONS' && (
        <OperationsQuestion round={round} canReview={canReview} onDone={refresh} />
      )}

      {round.opsVerdict && view !== 'AWAITING_OPERATIONS' && <OpsResponseSummary round={round} />}

      {view === 'AWAITING_OPERATIONS' && round.opsVerdict && (
        <div className="rounded-lg border border-border px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Your earlier response
          </p>
          <OpsResponseSummary round={round} bare />
        </div>
      )}

      {view === 'CLOSED' && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Outcome
          </p>
          <p className="mt-1 text-sm text-foreground">
            {round.commercialOutcome
              ? COMMERCIAL_OUTCOME_LABELS[round.commercialOutcome]
              : ROUND_STATE_LABELS[round.state]}
          </p>
          {round.closureMessage && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
              {round.closureMessage}
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Closed {fmtDateTime(round.closedAt)} · No further Operations action.
          </p>
        </div>
      )}

      {/* ── Conversation & notes ──────────────────────────────────────────── */}
      <Conversation
        entries={entriesQuery.data ?? []}
        loading={entriesQuery.isLoading}
        failed={entriesQuery.isError}
        onRetry={() => void entriesQuery.refetch()}
      />

      {view !== 'CLOSED' && canReview && (
        <InternalNoteForm inspectionId={inspectionId} roundId={round.id} onDone={refresh} />
      )}

      {earlierRounds.length > 0 && <EarlierRounds rounds={earlierRounds} />}
    </div>
  );
}

// ── The question and the form ───────────────────────────────────────────────

function OperationsQuestion({
  round,
  canReview,
  onDone,
}: {
  round: ModificationRound;
  canReview: boolean;
  onDone: () => void;
}) {
  return (
    <section
      aria-labelledby="ops-feasibility-heading"
      className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-4"
    >
      <h3 id="ops-feasibility-heading" className="text-sm font-semibold text-foreground">
        Operations Review — Feasibility
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Is the requested change technically possible, and what would it change about the work?
        Commercial will handle the customer&rsquo;s reply and any quotation revision.
      </p>
      {canReview ? (
        <VerdictForm inspectionId={round.inspectionId} roundId={round.id} onDone={onDone} />
      ) : (
        <p role="status" className="mt-3 text-sm text-muted-foreground">
          Waiting for an Operations user with feasibility-review permission to respond.
        </p>
      )}
    </section>
  );
}

function VerdictForm({
  inspectionId,
  roundId,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  onDone: () => void;
}) {
  const [form, setForm] = useState<VerdictFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const submitting = useRef(false);

  const set = <K extends keyof VerdictFormState>(key: K, value: VerdictFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const mutation = useMutation({
    mutationFn: (payload: RecordOpsVerdictPayload) =>
      inspectionModificationService.recordOpsVerdict(inspectionId, roundId, payload),
    onSuccess: (_round, payload) => {
      toast.success(
        payload.verdict === 'MORE_INFORMATION_REQUIRED'
          ? 'Question sent to Commercial to relay to the customer'
          : 'Feasibility response sent to Commercial',
      );
      setForm(EMPTY_FORM);
      setErrors({});
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to send the feasibility response'));
      const status = apiErrorStatus(e);
      // A stale or vanished round: re-read so the screen shows the truth. The
      // user's text is kept (forms standard) in case the round is still theirs.
      if (status === 404 || status === 409) onDone();
    },
    onSettled: () => {
      submitting.current = false;
    },
  });

  const needs = form.verdict ? opsVerdictRequires(form.verdict) : null;
  const busy = mutation.isPending;

  const handleSubmit = async () => {
    if (submitting.current || busy) return;
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !form.verdict) return;
    const verdict = form.verdict;
    const ok = await swalConfirm({
      title:
        verdict === 'MORE_INFORMATION_REQUIRED'
          ? 'Ask the customer for more information?'
          : 'Send feasibility response to Commercial?',
      text:
        verdict === 'MORE_INFORMATION_REQUIRED'
          ? 'Commercial will relay your question to the customer. The request returns to Operations when they answer.'
          : `Your response — ${OPS_VERDICT_LABELS[verdict]} — goes to Commercial, who will respond to the customer.`,
      confirmText: 'Send',
    });
    if (!ok) return;
    if (submitting.current || mutation.isPending) return;
    submitting.current = true;
    mutation.mutate(toPayload(form, verdict));
  };

  return (
    <form
      className="mt-4 space-y-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <fieldset disabled={busy} className="space-y-4">
        <div>
          <p id="verdict-label" className="mb-2 text-sm font-medium text-foreground">
            Feasibility <span className="text-destructive">*</span>
          </p>
          <div
            role="radiogroup"
            aria-labelledby="verdict-label"
            aria-required="true"
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {OPS_VERDICTS.map((v) => {
              const selected = form.verdict === v;
              return (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => set('verdict', v)}
                  className={`min-h-[44px] rounded-lg border px-3 py-2 text-left transition-colors ${
                    selected
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="block text-sm font-medium">{OPS_VERDICT_LABELS[v]}</span>
                  <span className="block text-xs text-muted-foreground">
                    {OPS_VERDICT_HINTS[v]}
                  </span>
                </button>
              );
            })}
          </div>
          {errors.verdict && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {errors.verdict}
            </p>
          )}
        </div>

        <FormField
          label="Technical explanation"
          htmlFor="ops-technical-note"
          required
          error={errors.technicalNote}
        >
          <Textarea
            id="ops-technical-note"
            value={form.technicalNote}
            onChange={(e) => set('technicalNote', e.target.value)}
            rows={4}
            maxLength={MODIFICATION_TEXT_MAX}
            aria-required="true"
            placeholder="What was checked, and why the change is or is not possible…"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Internal. The customer sees this only if Commercial chooses to share it.
          </p>
        </FormField>

        {needs?.alternativeDescription && (
          <FormField
            label="Alternative that would work"
            htmlFor="ops-alternative"
            required
            error={errors.alternativeDescription}
          >
            <Textarea
              id="ops-alternative"
              value={form.alternativeDescription}
              onChange={(e) => set('alternativeDescription', e.target.value)}
              rows={3}
              maxLength={MODIFICATION_TEXT_MAX}
              aria-required="true"
              placeholder="Describe the alternative approach…"
            />
          </FormField>
        )}

        {needs?.clarificationQuestion && (
          <FormField
            label="Question for the customer"
            htmlFor="ops-clarification"
            required
            error={errors.clarificationQuestion}
          >
            <Textarea
              id="ops-clarification"
              value={form.clarificationQuestion}
              onChange={(e) => set('clarificationQuestion', e.target.value)}
              rows={3}
              maxLength={MODIFICATION_TEXT_MAX}
              aria-required="true"
              placeholder="What do you need to know before you can answer?"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Commercial relays this to the customer; Operations never messages the customer
              directly.
            </p>
          </FormField>
        )}

        {form.verdict && !needs?.clarificationQuestion && (
          <div className="space-y-3 rounded-lg border border-border px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Impact on the work (no pricing)
            </p>
            <div>
              <label
                htmlFor="ops-scope-impact"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Scope of work
              </label>
              <select
                id="ops-scope-impact"
                value={form.scopeImpact}
                onChange={(e) => set('scopeImpact', e.target.value as OpsScopeImpact | '')}
                className={SELECT_CLASS}
              >
                <option value="">Not stated</option>
                {SCOPE_IMPACTS.map((s) => (
                  <option key={s} value={s}>
                    {SCOPE_IMPACT_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
              <label className="flex min-h-[44px] items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.affectsParts}
                  onChange={(e) => set('affectsParts', e.target.checked)}
                />
                Affects parts
              </label>
              <label className="flex min-h-[44px] items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.affectsLabour}
                  onChange={(e) => set('affectsLabour', e.target.checked)}
                />
                Affects labour
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              These tell Commercial whether the quotation may need revisiting. Commercial alone
              decides what, if anything, changes commercially.
            </p>
          </div>
        )}
      </fieldset>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="submit" disabled={busy} aria-disabled={busy}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Sending…' : 'Send to Commercial'}
        </Button>
      </div>
    </form>
  );
}

// ── Read-only pieces ────────────────────────────────────────────────────────

function OpsResponseSummary({ round, bare = false }: { round: ModificationRound; bare?: boolean }) {
  if (!round.opsVerdict) return null;
  const body = (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">{OPS_VERDICT_LABELS[round.opsVerdict]}</p>
      {round.opsTechnicalNote && (
        <p className="whitespace-pre-wrap text-sm text-foreground">{round.opsTechnicalNote}</p>
      )}
      {round.opsAlternativeDescription && (
        <Field label="Alternative">
          <span className="whitespace-pre-wrap">{round.opsAlternativeDescription}</span>
        </Field>
      )}
      {(round.opsScopeImpact || round.opsAffectsParts || round.opsAffectsLabour) && (
        <p className="text-xs text-muted-foreground">
          {[
            round.opsScopeImpact ? SCOPE_IMPACT_LABELS[round.opsScopeImpact] : null,
            round.opsAffectsParts ? 'Affects parts' : null,
            round.opsAffectsLabour ? 'Affects labour' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Responded {fmtDateTime(round.opsRespondedAt)}</p>
    </div>
  );
  if (bare) return <div className="mt-2">{body}</div>;
  return (
    <section
      aria-label="Operations feasibility response"
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Operations response — sent to Commercial
      </p>
      {body}
    </section>
  );
}

function Conversation({
  entries,
  loading,
  failed,
  onRetry,
}: {
  entries: ModificationEntry[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="min-h-[44px] text-sm font-medium text-primary hover:underline"
      >
        {open ? 'Hide conversation' : `Show conversation (${entries.length})`}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Loading conversation…</p>}
          {failed && (
            <p className="text-sm text-muted-foreground">
              The conversation could not be loaded.{' '}
              <button type="button" className="text-primary hover:underline" onClick={onRetry}>
                Retry
              </button>
            </p>
          )}
          {!loading && !failed && entries.length === 0 && (
            <p className="text-sm text-muted-foreground">No entries yet.</p>
          )}
          {entries.map((e) => (
            <div key={e.id} className="rounded-lg border border-border px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-foreground">
                  {ENTRY_TYPE_LABELS[e.entryType] ?? e.entryType}
                </span>
                <span className="text-xs text-muted-foreground">
                  {e.authorKind === 'CUSTOMER'
                    ? 'Customer'
                    : e.authorKind === 'STAFF'
                      ? 'Staff'
                      : 'System'}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] ${
                    e.visibility === 'INTERNAL'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-green-500/10 text-green-700'
                  }`}
                >
                  {e.visibility === 'INTERNAL' ? 'Internal' : 'Customer-visible'}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {fmtDateTime(e.createdAt)}
                </span>
              </div>
              {e.body && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{e.body}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InternalNoteForm({
  inspectionId,
  roundId,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  onDone: () => void;
}) {
  const [body, setBody] = useState('');
  const mutation = useMutation({
    mutationFn: (text: string) =>
      inspectionModificationService.addOpsNote(inspectionId, roundId, text),
    onSuccess: () => {
      toast.success('Internal note added');
      setBody('');
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to add the note'));
      const status = apiErrorStatus(e);
      if (status === 404 || status === 409) onDone();
    },
  });
  const busy = mutation.isPending;
  return (
    <form
      className="space-y-2"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || !body.trim()) return;
        mutation.mutate(body.trim());
      }}
    >
      <FormField label="Internal note (never shown to the customer)" htmlFor="ops-internal-note">
        <Textarea
          id="ops-internal-note"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          maxLength={MODIFICATION_TEXT_MAX}
          disabled={busy}
          placeholder="Add context for Commercial or your team…"
        />
      </FormField>
      <div className="flex justify-end">
        <Button type="submit" variant="secondary" size="sm" disabled={busy || !body.trim()}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />}
          {busy ? 'Adding…' : 'Add internal note'}
        </Button>
      </div>
    </form>
  );
}

function EarlierRounds({ rounds }: { rounds: ModificationRound[] }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Earlier requests on this job
      </p>
      <ul className="space-y-1">
        {rounds.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm text-foreground">
            <span>Request #{r.roundNumber}</span>
            <StateBadge round={r} />
            {r.opsVerdict && (
              <span className="text-xs text-muted-foreground">
                Operations: {OPS_VERDICT_LABELS[r.opsVerdict]}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {fmtDateTime(r.closedAt ?? r.updatedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
